import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Campaign"` — plan unit P14-02, one campaign.
 *
 * WHAT IS ON IT: the header (list, name, state, start and finish), View results, Pause /
 * Resume, Download, the four figures, the questions, the outcomes, the settings in use, the
 * cost, and — below the design's panel — the people this campaign left out and why (AC-31).
 *
 * FOUR THINGS THIS SCREEN IS CAREFUL ABOUT.
 *
 * **1. Whose clock.** The prototype prints `Time zone · America/Los_Angeles` beside the
 * calling window, which reads as "the campaign rings on Pacific time". The opposite is true:
 * the hours are each RECIPIENT's own and a person whose zone is unknown is skipped, not rung.
 * The Time zone row prints the view's `timeZoneNote`, which says exactly that, and never a zone.
 *
 * **2. Nobody is called yet, and the header says so.** Creating a campaign queues its
 * dialling; the process that places the calls is plan-003's. The slice words that into
 * `etaText` and this screen prints it as given — it never invents a finish time — and a
 * campaign that has called nobody shows no outcome bars at all rather than four zeros.
 *
 * **3. There is no "Replace question set".** The design draws one (an upload panel with "Use this set"); the
 * product owner withdrew it (2026-10-09, answer 40): a campaign asks what its agent asks, and the questions change
 * only with the agent. (Until then it was a deferred panel, DD-14, `replaceCampaignQuestionSet` in `GATEWAY_GAP`.)
 *
 * **4. Money is minor units with its currency.** Every figure in the Cost block is a `Money`
 * from the view, formatted with the currency it carries; "per completed call" is `—` until a
 * call has completed, never a division by zero shown as zero.
 *
 * AMENDED 2026-09-23 (design fidelity, ADR 0018): the markup and sizes are the prototype's —
 * the 28 px title, the four 22 px figures, the question cards, and ONE full-width panel for
 * Outcomes, Settings in use and Cost ending in its ghost-button row. What the earlier render
 * added beside the design is gone ("Shares are of …", "· your tier allows 12"), and the people
 * this campaign left out (AC-31) open from that row's "People not called" button instead of
 * sitting below the page, so the page is the design's until someone asks why.
 *
 * WHAT THE DESIGN DRAWS THAT IS WORDED DIFFERENTLY, AND WHY.
 *
 *   · **"See every call from this campaign".** History cannot be filtered by campaign (no
 *     operation takes one), so the link opens the agent's History and says so: "See this
 *     agent's conversations". A link promising a filter that is not applied would mislead.
 *
 * AMENDED 2026-09-30 (plan-008 ST-UA, design §22.14): the campaign dialler rings people through the
 * account's own SIP trunk. Under the header, the dialling state says in words whether anybody is
 * being rung — "Dialling", "Paused", "Not started", "No trunk route", "Switched off", "Finished" —
 * and, when the campaign cannot dial, why ("Its SIP trunk is not verified yet…"). Settings in use
 * gains the number it calls from and the voicemail sentence; People not called names the refusals
 * the dialler met as well as AC-31's reasons. Resuming a campaign with no route is refused by the
 * API, and the refusal is printed in words.
 *
 * AMENDED 2026-10-03 (survey results, design §16.7.2 AS BUILT 2026-10-03): View results opens the Survey
 * results screen for this campaign, read from the survey interviewer's own answers (`survey_answer`)
 * rather than a survey round, which nothing wrote; Download writes this campaign's answers as CSV
 * through the same export (`requestSurveyDownload`, audited). Download waits for the first call.
 *
 * AMENDED 2026-10-04 (design §16.7.2 AS AMENDED 2026-10-04, product owner): a campaign of a survey agent
 * asks the agent's own questionnaire, so "Questions in this campaign" lists the agent's questions read-only
 * (number, text, kind, choices) with "The agent asks the questions it was built with. To change them,
 * rebuild the agent with a new document." and no "Replace question set"; a campaign created with typed
 * questions keeps showing them as before. Beside Pause, ONE way to stop it for good: **Delete campaign**
 * while nobody has been called (skips alone are not calls), **End campaign** once somebody has. Each
 * opens an in-page confirmation saying what happens — End: calls stop for good, a call in progress
 * finishes, the results are kept; Delete: nothing is lost because nobody was called. An ended campaign
 * reads "Ended", hides Pause and Resume and can only be read; after Delete the agent's Outbound tab opens.
 *
 * AMENDED 2026-10-09 (plan-015 CMP, G16, G17, owner answers 5 and 6): Settings in use says when it runs — "From
 * <day> at <time> to the end of <day>, in each person's local time" (or "until its last retry" with no end) — and,
 * until the campaign starts, **Edit campaign** opens New campaign as an edit (`/campaigns/$campaignId/edit`). It is
 * offered only while the API says `editable` and the gateway can save (`updateCampaign`); once the campaign has
 * started it can only be paused or ended. People not called names the end date's skip (`campaign_ended`).
 * AMENDED 2026-10-30 (owner): Edit campaign stays offered after the start, until the campaign finishes or is ended;
 * the edit then keeps the contact list and the start (`lockedField`).
 *
 * AMENDED 2026-10-09 (plan-015 QSET-OFF, owner answer 40, supersedes answer 8): "On a campaign we set an agent; we do
 * not change the questions — the campaign uses what the agent supports." No campaign offers "Replace question set" —
 * neither a survey agent's (QSET's panel, briefly drawn by INT-B, is gone) nor one created with typed questions
 * before 2026-10-04 (its deferred panel is gone too): "Questions in this campaign" lists the questions read-only.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Dialog, Icon, Tag } from "@wetalk/ui";
import { useId, useState, type ReactNode } from "react";

import { VoiceAgentQuestionList } from "./voice-agent-question-list.js";
import { CampaignSkipPanel } from "./campaign-skip-panel.js";
import {
  VOICE_AGENT_QUESTION_NOTE,
  DELETE_CONFIRMATION,
  END_CONFIRMATION,
  VOICEMAIL_SENTENCE,
  shownCampaignOutcomeOf,
  campaignPriceUnavailableTextOf,
  campaignRefusalText,
  campaignRunText,
  campaignStateLabel,
  campaignStateTone,
  campaignLineScheduleTitle,
  countText,
  diallingStateLabel,
  diallingStateSentence,
  diallingStateTone,
  outboundRateTextOf,
  pauseActionOf,
  questionKindTone,
  sipTrunkRouteSentence,
  stopActionOf,
} from "./campaign-copy.js";
import { SurveyDownloadDialog } from "./survey-download-dialog.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { QUERY_KEY, campaignQuery, creditBalanceQuery, surveyResultQuery } from "../../api/query.js";
import { basisPointOf, percentText, questionKindLabel } from "../../api/outbound-view.js";
import { meterStyle } from "../../meter.js";
import { formatMoney } from "../../money.js";
import { talkTimeText } from "../../talk-time.js";
import type { CampaignQuestionRowView, SurveyResultFilterView } from "../../api/gateway.js";
import type { CampaignDetailView } from "../../api/slice/campaign.js";

import "./campaign.css";

export interface CampaignScreenProps {
  readonly campaignId: string;
}

export function CampaignScreen(props: CampaignScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const campaign = useQuery(campaignQuery(gateway, props.campaignId));

  if (campaign.isError) {
    return (
      <section className="c-page camp-page" data-screen-label="Campaign">
        <Link to="/agents" className="c-backlink camp-back">
          <Icon glyph={GLYPH.arrowLeft} size="sm" />
          {portalText("operations", "back_to_agents_c3c9c2a6")}</Link>
        <h1 className="c-camp-title">{portalText("operations", "campaign_69390e16")}</h1>
        <p className="c-note c-note-critical">
          {portalText("operations", "we_could_not_load_this_campaign_so_nothing_is_shown_rather_4b87fc78")}{" "}
          {campaignRefusalText(campaign.error)}
        </p>
      </section>
    );
  }
  if (campaign.data === undefined) {
    return (
      <section className="c-page camp-page" data-screen-label="Campaign">
        <p className="c-note">{portalText("operations", "opening_the_campaign_6a784727")}</p>
      </section>
    );
  }
  return <CampaignBody campaign={campaign.data} />;
}

// -------------------------------------------------------------------------------------------
// One campaign
// -------------------------------------------------------------------------------------------

function CampaignBody({ campaign }: { readonly campaign: CampaignDetailView }): ReactNode {
  usePortalTranslation("operations");
  // 2026-10-04: what a new conversation would be told now — a running campaign whose next call the
  // balance refuses says why, in the API's words (held by a conversation in progress, no credit, …).
  const gateway = useGateway();
  const balance = useQuery({ ...creditBalanceQuery(gateway), retry: false });
  const moneyRefusal = balance.data?.refusal ?? null;
  const queryClient = useQueryClient();
  const downloadNoteId = useId();
  const returningHeadingId = useId();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [skipsOpen, setSkipsOpen] = useState(false);
  // 2026-11-03: every role that reads the results may download them (the API's roles; a Billing member may not).
  const member = useMemberCan();
  const skipsId = useId();

  // Read only while the download dialog is open: its census sentence names this count.
  const downloadFilter: SurveyResultFilterView = {
    range: "all",
    includeIsTest: false,
    campaignId: campaign.campaignId,
    languageCode: null,
    variant: null,
  };
  const downloadResult = useQuery({
    ...surveyResultQuery(gateway, campaign.voiceAgentId, downloadFilter),
    enabled: downloadOpen,
  });

  const pauseAction = pauseActionOf(campaign.state);
  const pause = useMutation<CampaignDetailView, Error, boolean>({
    mutationFn: (paused) => gateway.setCampaignPaused(campaign.campaignId, paused),
    onSuccess: (next) => {
      queryClient.setQueryData(campaignQuery(gateway, campaign.campaignId).queryKey, next);
      void queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.campaign, next.voiceAgentId] });
    },
  });

  // 2026-10-04: End (for good) or Delete (nobody called yet) — one of them, after an in-page confirmation.
  const navigate = useNavigate();
  const stopAction = stopActionOf(campaign);
  const [confirming, setConfirming] = useState<"end" | "delete" | null>(null);
  const end = useMutation<CampaignDetailView, Error, undefined>({
    mutationFn: () => gateway.endCampaign(campaign.campaignId),
    onSuccess: (next) => {
      queryClient.setQueryData(campaignQuery(gateway, campaign.campaignId).queryKey, next);
      void queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.campaign, next.voiceAgentId] });
    },
  });
  const remove = useMutation<undefined, Error, undefined>({
    mutationFn: async () => {
      await gateway.deleteCampaign(campaign.campaignId);
      return undefined;
    },
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: campaignQuery(gateway, campaign.campaignId).queryKey });
      await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.campaign, campaign.voiceAgentId] });
      await navigate({ to: "/agents/$voiceAgentId", params: { voiceAgentId: campaign.voiceAgentId }, search: { tab: "outbound" } });
    },
  });
  const stopError = end.error ?? remove.error;

  // plan-015 CMP (G16, G17): the start and end on each person's own clock; `null` from a gateway that does not say.
  const runText = campaignRunText(campaign);

  const answeredText =
    campaign.calledCount === 0
      ? "—"
      : percentText(basisPointOf(campaign.connectedCount, campaign.calledCount));

  return (
    <section className="c-page camp-page" data-screen-label="Campaign">
      <Link
        to="/agents/$voiceAgentId"
        params={{ voiceAgentId: campaign.voiceAgentId }}
        search={{ tab: "outbound" }}
        className="c-backlink camp-back"
      >
        <Icon glyph={GLYPH.arrowLeft} size="sm" />
        {portalText("operations", "back_to_campaigns_ed9aa177")}</Link>

      <div className="c-headline-row camp-headline">
        <div className="c-camp-head">
          <p className="c-camp-kicker">{portalText("operations", "outbound_campaign_3505a509")}{campaign.contactListName}</p>
          <div className="c-camp-title-row">
            <h1 className="c-camp-title">{campaign.name}</h1>
            <Tag status={campaignStateTone(campaign.state)}>{campaignStateLabel(campaign.state)}</Tag>
          </div>
          <p className="c-camp-when">
            {campaign.startedText} · {campaign.etaText}
          </p>
          <p className="c-camp-dialling">
            <Tag status={diallingStateTone(campaign.diallingState)}>{diallingStateLabel(campaign.diallingState)}</Tag>
            <span>
              {diallingStateSentence(campaign.diallingState)}
              {campaign.diallingState === "no_route" ? (
                <> {sipTrunkRouteSentence(campaign.sipTrunkRoute, campaign.callerIdE164)}</>
              ) : null}
              {campaign.state === "running" && moneyRefusal !== null ? <> {moneyRefusal.message}</> : null}
            </span>
          </p>
        </div>
        <div className="c-camp-actions">
          <Link
            to="/campaigns/$campaignId/results"
            params={{ campaignId: campaign.campaignId }}
            search={{ range: "all", includeIsTest: false, language: "", variant: "", view: "question", status: "all" }}
            className="btn btn-primary"
          >
            <Icon glyph={GLYPH.listChecks} size="md" />
            {portalText("operations", "view_results_d766a0ac")}</Link>
          {/* plan-015 CMP (G17), amended 2026-10-30: until it finishes, the campaign can be changed. */}
          {campaign.editable ? (
            <Link to="/campaigns/$campaignId/edit" params={{ campaignId: campaign.campaignId }} className="btn btn-secondary">
              <Icon glyph={GLYPH.pencilSimple} size="md" />
              {portalText("operations", "edit_campaign_e73cfe91")}</Link>
          ) : null}
          {pauseAction === null ? null : (
            <Button
              variant="secondary"
              disabled={pause.isPending}
              onClick={() => {
                pause.mutate(pauseAction === "pause");
              }}
            >
              <Icon glyph={pauseAction === "resume" ? GLYPH.play : GLYPH.pause} size="md" />
              {pause.isPending
                ? portalText("operations", "saving_56a2285c")
                : pauseAction === "pause"
                  ? portalText("operations", "pause_campaign_81b2c204")
                  : portalText("operations", "resume_campaign_c8bb46f4")}
            </Button>
          )}
          {stopAction === null ? null : (
            <Button
              variant="secondary"
              disabled={end.isPending || remove.isPending}
              onClick={() => {
                end.reset();
                remove.reset();
                setConfirming(stopAction);
              }}
            >
              <Icon glyph={stopAction === "delete" ? GLYPH.trash : GLYPH.stopCircle} size="md" />
              {stopAction === "delete" ? portalText("operations", "delete_campaign_d26a8fba") : portalText("operations", "end_campaign_90b0709c")}
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={campaign.calledCount === 0 || !member.can("survey_result.export")}
            title={member.whyNot("survey_result.export") ?? undefined}
            aria-describedby={campaign.calledCount === 0 ? downloadNoteId : undefined}
            onClick={() => {
              setDownloadOpen(true);
            }}
          >
            <Icon glyph={GLYPH.downloadSimple} size="md" />
            {portalText("operations", "download_a479c9c3")}</Button>
        </div>
      </div>

      {campaign.calledCount === 0 ? (
        <p className="c-note" id={downloadNoteId}>
          {portalText("operations", "nobody_has_been_called_yet_so_there_are_no_answers_to_down_c2912c4b")}</p>
      ) : null}
      {pause.isError ? (
        <p className="c-note c-note-critical">
          {portalText("operations", pauseAction === "resume" ? "campaign.failure.resumed" : "campaign.failure.paused")}{" "}
          {campaignRefusalText(pause.error)}
        </p>
      ) : null}
      {stopError === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {portalText("operations", remove.error === null ? "campaign.failure.ended" : "campaign.failure.deleted")} {campaignRefusalText(stopError)}
        </p>
      )}
      {/* One confirmation in the page at a time, mounted only while it is asked. */}
      {confirming === null ? null : (
        <Dialog
          open
          title={confirming === "end" ? portalText("operations", "end_value_850987d8", { v0: String(campaign.name) }) : portalText("operations", "delete_value_26a19215", { v0: String(campaign.name) })}
          onClose={() => {
            setConfirming(null);
          }}
          actions={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirming(null);
                }}
              >
                {portalText("operations", "keep_it_d9f36924")}</Button>
              <Button
                variant="primary"
                onClick={() => {
                  const asked = confirming;
                  setConfirming(null);
                  if (asked === "end") end.mutate(undefined);
                  else remove.mutate(undefined);
                }}
              >
                {confirming === "end" ? portalText("operations", "end_campaign_90b0709c") : portalText("operations", "delete_campaign_d26a8fba")}
              </Button>
            </>
          }
        >
          <p>{confirming === "end" ? END_CONFIRMATION() : DELETE_CONFIRMATION()}</p>
        </Dialog>
      )}

      <div className="c-camp-stats">
        <Stat value={countText(campaign.personCount)} label={portalText("operations", "on_the_list_f6ba2f3e")} />
        <Stat value={countText(campaign.calledCount)} label={portalText("operations", "called_80bb4391")} />
        <Stat value={countText(campaign.completedCount)} label={portalText("operations", "completed_1798b3ba")} />
        <Stat value={answeredText} label={portalText("operations", "answered_e0aafffa")} />
      </div>

      {campaign.questionSource === "voice_agent_questionnaire" ? (
        <>
          <div className="c-camp-section-head">
            <div>
              <p className="c-camp-section-title">{portalText("operations", "questions_in_this_campaign_8e87669b")}</p>
              <p className="c-camp-section-dek">
                {campaign.questionSetText}. {VOICE_AGENT_QUESTION_NOTE()}
              </p>
            </div>
          </div>
          {campaign.voiceAgentQuestion.length === 0 ? (
            <div className="c-panel c-panel-dashed">
              <p className="c-empty-title">{portalText("operations", "no_questions_on_file_117f140b")}</p>
              <p className="c-empty-dek">
                {portalText("operations", "the_agent_s_published_version_carries_no_questionnaire_so_7ed8d40b")}</p>
            </div>
          ) : (
            <VoiceAgentQuestionList question={campaign.voiceAgentQuestion} look="card" />
          )}
        </>
      ) : (
        <>
      {/* A campaign created with typed questions (before 2026-10-04) keeps showing them. */}
      <div className="c-camp-section-head">
        <div>
          <p className="c-camp-section-title">{portalText("operations", "questions_in_this_campaign_8e87669b")}</p>
          <p className="c-camp-section-dek">{campaign.questionSetText}</p>
        </div>
      </div>

      {campaign.question.length === 0 ? (
        <div className="c-panel c-panel-dashed">
          <p className="c-empty-title">{portalText("operations", "no_questions_on_file_117f140b")}</p>
          <p className="c-empty-dek">{portalText("operations", "this_campaign_has_no_active_questions_so_the_agent_has_not_fe6081ff")}</p>
        </div>
      ) : (
        <>
          <QuestionList question={campaign.question.filter((one) => one.questionSet === "primary")} />
          {campaign.question.some((one) => one.questionSet === "returning") ? (
            // Register 0z.4: the returning set is its own list, numbered from 1, never mixed into
            // the primary one — a person already surveyed is asked these INSTEAD.
            <section className="c-camp-returning-set" aria-labelledby={returningHeadingId}>
              <h3 className="c-camp-returning-set-title" id={returningHeadingId}>
                {portalText("operations", "for_people_already_surveyed_86432a4d")}</h3>
              <p className="c-camp-section-dek">
                {portalText("operations", "asked_instead_of_the_questions_above_when_someone_on_the_l_38117999")}</p>
              <QuestionList question={campaign.question.filter((one) => one.questionSet === "returning")} />
            </section>
          ) : null}
        </>
      )}

        </>
      )}

      <div className="c-camp-detail">
        <p className="c-camp-eyebrow">{portalText("operations", "outcomes_6848ce55")}</p>
        {campaign.calledCount === 0 ? (
          <p className="c-camp-quiet">
            {portalText("operations", "nobody_has_been_called_yet_so_there_are_no_outcomes_to_sho_842f0db4")}</p>
        ) : (
          <div className="c-camp-outcomes">
            {/* plan-031 VMD-WAPP: Voicemail is a bar of its own, beside No answer — never "connected". */}
            {shownCampaignOutcomeOf(campaign.outcome, campaign.voicemailDetection).map((one) => (
              <span className="c-camp-outcome" key={one.code}>
                <span className="c-camp-outcome-head">
                  <span>{one.label}</span>
                  <span className="c-camp-outcome-figure">
                    {countText(one.count)} · {percentText(one.shareBasisPoint)}
                  </span>
                </span>
                <span className="c-camp-bar" aria-hidden="true">
                  <span className="c-camp-bar-fill" style={meterStyle(one.shareBasisPoint / 100)} />
                </span>
              </span>
            ))}
          </div>
        )}
        {/* plan-033 WAPP-RET (design §44.10): people who rang the campaign's number back, counted above under what
            happened on that conversation — the line only when there are any. */}
        {campaign.calledBackCount > 0 ? (
          <p className="c-camp-quiet" title={portalText("shell", "campaign_outcome.called_back_title")}>
            {portalText("shell", "campaign_outcome.called_back", { count: countText(campaign.calledBackCount) })}
          </p>
        ) : null}

        <p className="c-camp-eyebrow">{portalText("operations", "settings_in_use_2994fa72")}</p>
        <dl className="c-camp-settings">
          {runText === null ? null : (
            <>
              <dt>{portalText("operations", "runs_fcde5c32")}</dt>
              <dd>{runText}</dd>
            </>
          )}
          <dt>{portalText("operations", "calling_window_ff8b1821")}</dt>
          <dd>{campaign.schedule.windowText}</dd>
          <dt>{portalText("operations", "time_zone_eea79afd")}</dt>
          <dd>{campaign.schedule.timeZoneNote}</dd>
          <dt>{portalText("operations", "at_once_1e5121a7")}</dt>
          <dd
            title={
              // Campaign fix-up 2026-10-28: whose limit it is — the agent's own (ADR 0045) or the workspace's when that
              // is the lowest, else the organization's.
              campaignLineScheduleTitle(campaign.schedule)
            }
          >
            {countText(campaign.schedule.simultaneousCall)} {portalText("operations", "calls_51b6cb25")}</dd>
          <dt>{portalText("operations", "retries_13093e56")}</dt>
          <dd>{campaign.schedule.retryText}</dd>
          <dt>{portalText("operations", "pace_7a9a6226")}</dt>
          <dd>{campaign.schedule.paceText}</dd>
          <dt>{portalText("operations", "calls_from_e7a284a8")}</dt>
          <dd title={sipTrunkRouteSentence(campaign.sipTrunkRoute, campaign.callerIdE164)}>
            {campaign.callerIdE164 ?? portalText("operations", "no_caller_id_number_cd0295ee")}
            {campaign.sipTrunkRoute === "ready" ? portalText("operations", "your_sip_trunk_4921e488") : ""}
          </dd>
          <dt>{portalText("operations", "voicemail_8fbf69dc")}</dt>
          <dd>{VOICEMAIL_SENTENCE(campaign.voicemailDetection)}</dd>
        </dl>

        <p className="c-camp-eyebrow">{portalText("operations", "cost_64ae43e8")}</p>
        <div className="c-camp-cost">
          <Stat value={formatMoney(campaign.cost.spent)} label={portalText("operations", "value_so_far_0c01ee43", { v0: String(talkTimeText(campaign.cost.talkSecond)) })} small />
          <Stat
            value={campaign.cost.perCompleted === null ? "—" : formatMoney(campaign.cost.perCompleted)}
            label={
              campaign.cost.perCompleted === null
                ? portalText("operations", "per_completed_call_none_completed_yet_ff30d45d")
                : portalText("operations", "per_completed_call_9d75d531")
            }
            small
          />
          <Stat value={formatMoney(campaign.cost.projected)} label={portalText("operations", "projected_total_577c2e1e")} small />
        </div>
        {/* plan-017 (Q5, OQ-1): each call is priced at its own pin; the projection at the route's outbound rate, named. */}
        <p className="c-camp-hint">{outboundRateTextOf(campaign.cost.talkRateKind, campaign.cost.talkRatePerMinute)}</p>
        {campaign.cost.priceUnavailableCount === 0 ? null : (
          <p className="c-camp-hint">{campaignPriceUnavailableTextOf(campaign.cost.priceUnavailableCount)}</p>
        )}
        <div className="c-camp-foot">
          <Link
            to="/agents/$voiceAgentId"
            params={{ voiceAgentId: campaign.voiceAgentId }}
            search={{ tab: "history" }}
            className="btn btn-ghost"
          >
            {portalText("operations", "see_this_agent_s_conversations_cf6417eb")}</Link>
          {/* AC-31: who this campaign left out, and why — one click away, not below the page. */}
          <Button
            variant="ghost"
            aria-expanded={skipsOpen}
            aria-controls={skipsId}
            onClick={() => {
              setSkipsOpen((open) => !open);
            }}
          >
            {portalText("operations", "people_not_called_81b8df96")}</Button>
        </div>
      </div>

      {skipsOpen ? (
        <div id={skipsId}>
          <CampaignSkipPanel campaignId={campaign.campaignId} />
        </div>
      ) : null}

      {/* AMENDED 2026-10-03: the download is this campaign's survey answers (`survey_answer`), the same
          file the Survey results screen offers with its filters at their defaults. */}
      <SurveyDownloadDialog
        open={downloadOpen}
        voiceAgentId={campaign.voiceAgentId}
        filter={downloadFilter}
        conversationCount={downloadResult.data?.conversationCount ?? 0}
        onClose={() => {
          setDownloadOpen(false);
        }}
      />
    </section>
  );
}

/** One question set, in its own order. */
function QuestionList({ question }: { readonly question: readonly CampaignQuestionRowView[] }): ReactNode {
  usePortalTranslation("operations");
  return (
    <ol className="c-camp-questions">
      {question.map((one) => (
        <li className="c-camp-question" key={one.campaignQuestionId}>
          <span className="c-camp-question-n">{one.ordinal}</span>
          <span className="c-camp-question-text">{one.text}</span>
          <span className="c-camp-question-meta">
            <Tag tone={questionKindTone(one.kind)}>{questionKindLabel(one.kind)}</Tag>
            <span className="c-camp-question-count">{countText(one.answeredCount)} {portalText("operations", "answered_76e806ba")}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function Stat({
  value,
  label,
  small = false,
}: {
  readonly value: string;
  readonly label: string;
  readonly small?: boolean;
}): ReactNode {
  usePortalTranslation("operations");
  return (
    <span className={small ? "c-camp-stat c-camp-stat-small" : "c-camp-stat"}>
      <span className="c-camp-stat-value">{value}</span>
      <span className="c-camp-stat-label">{label}</span>
    </span>
  );
}

